import React, { Suspense, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  BeachAccess,
  ExitToApp,
  Groups,
  Person,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import ExitEntryListPage from '../ExitEntry/ExitEntryListPage';
import ManagerRequestsList from './ManagerRequestsList';
import MyVacationRequests from './MyVacationRequests';
import { PageHeader, PageLayout, SectionCard } from '../../components/ui';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch() {}

  render() {
    if (this.state.hasError) {
      return (
        <Alert severity="error" sx={{ borderRadius: '8px' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Unable to render this section
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {this.state.error?.message || 'Unknown error'}
          </Typography>
        </Alert>
      );
    }

    return this.props.children;
  }
}

function LoadingBlock({ label = 'Loading...' }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, py: 2 }}>
      <CircularProgress size={20} />
      <Typography variant="body2">{label}</Typography>
    </Box>
  );
}

function SegmentedTabs({ value, onChange, children, sx }) {
  const theme = useTheme();

  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={(_, nextValue) => nextValue && onChange(nextValue)}
      sx={{
        gap: 0.75,
        flexWrap: 'wrap',
        '& .MuiToggleButton-root': {
          px: 1.35,
          py: 0.82,
          border: '1px solid',
          borderColor: alpha(theme.palette.primary.main, 0.16),
          borderRadius: '8px !important',
          color: 'text.secondary',
          fontWeight: 800,
          textTransform: 'none',
          backgroundColor: alpha(theme.palette.background.paper, 0.72),
          '&.Mui-selected': {
            color: 'primary.main',
            borderColor: alpha(theme.palette.primary.main, 0.34),
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
          },
        },
        ...sx,
      }}
    >
      {children}
    </ToggleButtonGroup>
  );
}

function VacationContainer({ initialTab = 'team', onTabChange }) {
  const [vacationTab, setVacationTab] = useState(initialTab);
  const handleVacationTabChange = (nextTab) => {
    setVacationTab(nextTab);
    onTabChange?.(nextTab);
  };

  return (
    <Box>
      <SegmentedTabs
        value={vacationTab}
        onChange={handleVacationTabChange}
        sx={{ mb: 1.4 }}
      >
        <ToggleButton value="team" aria-label="Team Requests">
          <Groups sx={{ mr: 1 }} fontSize="small" />
          Team Requests
        </ToggleButton>
        <ToggleButton value="personal" aria-label="My Requests">
          <Person sx={{ mr: 1 }} fontSize="small" />
          My Requests
        </ToggleButton>
      </SegmentedTabs>

      {vacationTab === 'team' ? (
        <ErrorBoundary>
          <Suspense fallback={<LoadingBlock label="Loading team requests..." />}>
            <ManagerRequestsList rows={[]} />
          </Suspense>
        </ErrorBoundary>
      ) : (
        <ErrorBoundary>
          <Suspense fallback={<LoadingBlock label="Loading my vacation requests..." />}>
            <MyVacationRequests rows={[]} />
          </Suspense>
        </ErrorBoundary>
      )}
    </Box>
  );
}

export default function ManagerVacationRequestsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialVacationTab = searchParams.get('view') === 'history' ? 'personal' : 'team';
  const [mainTab, setMainTab] = useState('vacation');

  return (
    <PageLayout backgroundAccent compact>
      <PageHeader
        compact
        overline="Manager"
        title="Approval workspace"
        subtitle="Review team vacation requests, track your own records, and keep exit-entry history in one focused manager surface."
        actions={[
          <Button key="dashboard" variant="outlined" onClick={() => navigate('/manager/dashboard')}>
            Dashboard
          </Button>,
        ]}
      />

      <SectionCard
        tone="dashboard"
        title="Request queues"
        subheader="Switch between approval work and personal records without leaving this page."
        sx={{ borderRadius: '8px' }}
        contentSx={{ p: { xs: 1.1, sm: 1.25 } }}
      >
        <Stack spacing={1.4}>
          <SegmentedTabs
            value={mainTab}
            onChange={setMainTab}
          >
            <ToggleButton value="vacation" aria-label="Vacation Management">
              <BeachAccess sx={{ mr: 1 }} fontSize="small" />
              Vacation
            </ToggleButton>
            <ToggleButton value="exit-entry" aria-label="My Exit Entry Records">
              <ExitToApp sx={{ mr: 1 }} fontSize="small" />
              Exit/Entry
            </ToggleButton>
          </SegmentedTabs>

          {mainTab === 'vacation' ? (
            <Box id="panel-vacation" role="tabpanel" aria-labelledby="tab-vacation">
              <ErrorBoundary>
                <Suspense fallback={<LoadingBlock label="Loading vacation panels..." />}>
                  <VacationContainer initialTab={initialVacationTab} />
                </Suspense>
              </ErrorBoundary>
            </Box>
          ) : (
            <Box id="panel-exit-entry" role="tabpanel" aria-labelledby="tab-exit-entry">
              <ErrorBoundary>
                <Suspense fallback={<LoadingBlock label="Loading exit/entry records..." />}>
                  <ExitEntryListPage rows={[]} />
                </Suspense>
              </ErrorBoundary>
            </Box>
          )}
        </Stack>
      </SectionCard>
    </PageLayout>
  );
}
